import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiDownload,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiAlertCircle,
  FiShield,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  fetchAllCompanyUsers,
  isWorkforcePerson,
} from '../utils/workforcePeople';
import '../styles/LicencesCompliance.css';

const GET_DOCS = `
  query GetWorkforceDocuments {
    getWorkforceDocuments {
      id
      userId
      employeeCode
      employeeName
      documentType
      cleaningLicense
      verificationStatus
      expiryDate
      siteReference
      createdAt
    }
  }
`;

const GET_USERS = `
  query ComplianceUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        employeeCode
        branchId
        visaStatus
        visaExpiryDay
        roles
        role
      }
      totalPages
    }
  }
`;

const GET_BRANCHES = `
  query ComplianceBranches {
    getMyCompanyBranches {
      id
      name
      location
    }
  }
`;

const AVATARS = ['#60a5fa', '#34d399', '#a78bfa', '#f472b6', '#fb923c', '#38bdf8'];

function initials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';
}

function avatarColor(name) {
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i) * 17) % AVATARS.length;
  return AVATARS[h];
}

function daysUntil(dateValue) {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function formatDate(dateValue) {
  if (!dateValue) return '—';
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function statusFromDays(days) {
  if (days === null) return { status: 'missing', label: 'Missing', daysLabel: '—' };
  if (days < 0) {
    return {
      status: 'expired',
      label: 'Expired',
      daysLabel: `${Math.abs(days)}d overdue`,
    };
  }
  if (days <= 30) {
    return { status: 'expiring', label: 'Expiring', daysLabel: `${days}d` };
  }
  return { status: 'valid', label: 'Valid', daysLabel: `${days}d` };
}

async function fetchAllUsers() {
  try {
    return await fetchAllCompanyUsers(graphqlRequest, GET_USERS);
  } catch (err) {
    const msg = String(err?.message || err || '');
    if (/branchId/i.test(msg)) {
      const fallback = GET_USERS.replace(/\s*branchId\s*/, '\n');
      return fetchAllCompanyUsers(graphqlRequest, fallback);
    }
    throw err;
  }
}

export default function LicencesCompliance({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docsData, users, branchesData] = await Promise.all([
        graphqlRequest(GET_DOCS),
        fetchAllUsers(),
        graphqlRequest(GET_BRANCHES).catch(() => ({ getMyCompanyBranches: [] })),
      ]);
      const docs = Array.isArray(docsData?.getWorkforceDocuments)
        ? docsData.getWorkforceDocuments
        : [];
      const workforceUsers = users.filter(isWorkforcePerson);
      const branchById = new Map(
        (branchesData?.getMyCompanyBranches || []).map((b) => [
          b.id,
          b.name || b.location || '—',
        ]),
      );
      const usersById = new Map(workforceUsers.map((u) => [u.id, u]));
      const usersByCode = new Map(
        workforceUsers
          .filter((u) => u.employeeCode)
          .map((u) => [String(u.employeeCode).toUpperCase(), u]),
      );

      const resolveBranch = (user) => {
        if (!user?.branchId) return '—';
        return branchById.get(user.branchId) || '—';
      };

      const tracker = [];

      workforceUsers.forEach((u) => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
        if (u.visaExpiryDay || u.visaStatus) {
          const days = daysUntil(u.visaExpiryDay);
          const meta = statusFromDays(u.visaExpiryDay ? days : null);
          tracker.push({
            id: `visa-${u.id}`,
            name,
            documentType: u.visaStatus || 'Working Visa',
            branch: resolveBranch(u),
            expiryDate: u.visaExpiryDay,
            ...meta,
            avatar: avatarColor(name),
          });
        }
      });

      docs.forEach((doc) => {
        const name = doc.employeeName || 'Unknown';
        const owner =
          usersById.get(doc.userId) ||
          usersByCode.get(String(doc.employeeCode || '').toUpperCase()) ||
          null;
        const expiry = doc.expiryDate ? new Date(doc.expiryDate) : null;
        const days = daysUntil(expiry);
        const verified =
          String(doc.verificationStatus || '').toLowerCase() === 'verified';
        const meta = verified
          ? expiry
            ? statusFromDays(days)
            : { status: 'valid', label: 'Verified', daysLabel: 'No expiry' }
          : { status: 'missing', label: 'Pending review', daysLabel: '—' };
        tracker.push({
          id: doc.id,
          name,
          documentType: doc.documentType || 'Document',
          branch: resolveBranch(owner),
          expiryDate: expiry,
          ...meta,
          avatar: avatarColor(name),
        });
      });

      // Sort: expired first, then expiring, then missing, then valid
      const rank = { expired: 0, expiring: 1, missing: 2, valid: 3 };
      tracker.sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9));
      setRows(tracker);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to load compliance data.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const refreshQuietly = () => {
      void load();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshQuietly();
    };
    window.addEventListener('focus', refreshQuietly);
    window.addEventListener('workforce-documents-refresh', refreshQuietly);
    window.addEventListener('admin-backend-notifications-refresh', refreshQuietly);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = window.setInterval(refreshQuietly, 25000);
    return () => {
      window.removeEventListener('focus', refreshQuietly);
      window.removeEventListener('workforce-documents-refresh', refreshQuietly);
      window.removeEventListener(
        'admin-backend-notifications-refresh',
        refreshQuietly,
      );
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [load]);

  const stats = useMemo(() => {
    const compliant = rows.filter((r) => r.status === 'valid').length;
    const expiring = rows.filter((r) => r.status === 'expiring').length;
    const expired = rows.filter((r) => r.status === 'expired').length;
    const missing = rows.filter((r) => r.status === 'missing').length;
    const total = rows.length || 1;
    const pct = Math.round((compliant / total) * 100);
    return [
      { id: 'compliant', label: 'Compliant', value: compliant, icon: FiCheckCircle, tone: 'green' },
      { id: 'expiring', label: 'Expiring (30d)', value: expiring, icon: FiAlertTriangle, tone: 'orange' },
      { id: 'expired', label: 'Expired', value: expired, icon: FiXCircle, tone: 'red' },
      { id: 'missing', label: 'Missing', value: missing, icon: FiAlertCircle, tone: 'pink' },
      { id: 'pct', label: 'Compliance %', value: `${pct}%`, icon: FiShield, tone: 'blue' },
    ];
  }, [rows]);

  return (
    <div className={`lc-page${embedded ? ' lc-page--embedded' : ''}`}>
      <header className="lc-header">
        <div>
          {!embedded && <h1>Licences & Compliance</h1>}
          <p>
            {embedded
              ? 'Track visa, licence and compliance expiry across the workforce.'
              : 'Enterprise compliance tracking across all branches'}
          </p>
        </div>
        <button type="button" className="lc-btn" onClick={() => toast.info('Compliance report export coming soon.')}>
          <FiDownload />
          Compliance Report
        </button>
      </header>

      <section className="lc-stats">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.id} className="lc-stat">
              <div className={`lc-stat-icon lc-stat-icon--${s.tone}`}>
                <Icon />
              </div>
              <div>
                <div className="lc-stat-value">{s.value}</div>
                <div className="lc-stat-label">{s.label}</div>
              </div>
            </article>
          );
        })}
      </section>

      <section className="lc-panel">
        <h2>Expiry Tracker</h2>
        <div className="lc-table-wrap">
          <table className="lc-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Document Type</th>
                <th>Branch</th>
                <th>Expiry Date</th>
                <th>Days Left</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="lc-empty">Loading compliance records…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="lc-empty">
                    No compliance records yet. Add workforce documents or visa details on team members.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="lc-employee">
                        <span className="lc-avatar" style={{ background: row.avatar }}>
                          {initials(row.name)}
                        </span>
                        <span>{row.name}</span>
                      </div>
                    </td>
                    <td>{row.documentType}</td>
                    <td>{row.branch}</td>
                    <td>
                      {row.status === 'expired' && row.expiryDate
                        ? `Expired ${formatDate(row.expiryDate)}`
                        : formatDate(row.expiryDate)}
                    </td>
                    <td className={`lc-days lc-days--${row.status}`}>{row.daysLabel}</td>
                    <td>
                      <span className={`lc-badge lc-badge--${row.status}`}>{row.label}</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="lc-link"
                        onClick={() => toast.success(`Reminder queued for ${row.name}.`)}
                      >
                        Send Reminder
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
